import React, { useEffect, useState } from 'react';
import ConfirmationModal from "../components/ConfirmationModal";
import { TableActionColumnHeader } from "../components/TableShell";
import ShieldCareSectionTabs, { SHIELD_CARE_TAB_MEDICATIONS } from "../components/ShieldCareSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import { IconEdit, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

const defaultPrescription = () => ({
  doctorId: '',
  startDate: '',
  endDate: '',
  frequency: '',
  timesPerDay: '',
  scheduledTimes: [],
  quantityPerDose: '',
  totalPrescribedQuantity: '',
  refillsAllowed: '',
});

const defaultPharmacy = () => ({
  pharmacyId: '',
  lastRefillDate: '',
  daysSupply: '',
  remainingQuantity: '',
  refillReminderThresholdDays: '',
});

function ShieldCareMedications() {
  const [currentObjs, setCurrentObjs] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [_id, setID] = useState(null);
  const [medicationName, setMedicationName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [strength, setStrength] = useState('');
  const [dosageForm, setDosageForm] = useState('');
  const [instructions, setInstructions] = useState('');
  const [prescription, setPrescription] = useState(defaultPrescription());
  const [pharmacy, setPharmacy] = useState(defaultPharmacy());
  const [status, setStatus] = useState('active');
  const [edit, setEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formExpanded, setFormExpanded] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [scheduledTimesStr, setScheduledTimesStr] = useState(''); // e.g. "08:00, 20:00"
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  useEffect(() => {
    getDocument();
    getDoctors();
    getPharmacies();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  function getDoctors() {
    if (!account_id) return;
    apiFetch(serverUrl + '/getDoctors', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setDoctors(Array.isArray(data) ? data : []))
      .catch(() => setDoctors([]));
  }

  function getPharmacies() {
    if (!account_id) return;
    apiFetch(serverUrl + '/getPharmacies', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setPharmacies(Array.isArray(data) ? data : []))
      .catch(() => setPharmacies([]));
  }

  function getDocument() {
    if (!account_id) return;
    apiFetch(serverUrl + '/getMedications', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function buildPrescriptionPayload() {
    const p = prescription;
    const times = (scheduledTimesStr || '')
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    return {
      doctorId: (p.doctorId != null ? String(p.doctorId) : '').trim(),
      startDate: (p.startDate != null ? String(p.startDate) : '').trim(),
      endDate: (p.endDate != null ? String(p.endDate) : '').trim(),
      frequency: (p.frequency != null ? String(p.frequency) : '').trim(),
      timesPerDay: p.timesPerDay !== '' && p.timesPerDay != null ? Number(p.timesPerDay) : undefined,
      scheduledTimes: times,
      quantityPerDose: p.quantityPerDose !== '' && p.quantityPerDose != null ? Number(p.quantityPerDose) : undefined,
      totalPrescribedQuantity: p.totalPrescribedQuantity !== '' && p.totalPrescribedQuantity != null ? Number(p.totalPrescribedQuantity) : undefined,
      refillsAllowed: p.refillsAllowed !== '' && p.refillsAllowed != null ? Number(p.refillsAllowed) : undefined,
    };
  }

  function buildPharmacyPayload() {
    const ph = pharmacy;
    return {
      pharmacyId: (ph.pharmacyId != null ? String(ph.pharmacyId) : '').trim(),
      lastRefillDate: (ph.lastRefillDate != null ? String(ph.lastRefillDate) : '').trim(),
      daysSupply: ph.daysSupply !== '' && ph.daysSupply != null ? Number(ph.daysSupply) : undefined,
      remainingQuantity: ph.remainingQuantity !== '' && ph.remainingQuantity != null ? Number(ph.remainingQuantity) : undefined,
      refillReminderThresholdDays: ph.refillReminderThresholdDays !== '' && ph.refillReminderThresholdDays != null ? Number(ph.refillReminderThresholdDays) : undefined,
    };
  }

  function createDocument() {
    const body = {
      account_id,
      medicationName,
      brandName,
      strength,
      dosageForm,
      instructions,
      prescription: buildPrescriptionPayload(),
      pharmacy: buildPharmacyPayload(),
      status: status || 'active',
    };
    apiFetch(serverUrl + '/createMedication', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Medication", message: data, mode: "alert", onConfirm: null });
        getDocument();
        collapseForm();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function updateDocument() {
    const body = {
      account_id,
      _id,
      medicationName,
      brandName,
      strength,
      dosageForm,
      instructions,
      prescription: buildPrescriptionPayload(),
      pharmacy: buildPharmacyPayload(),
      status: status || 'active',
    };
    apiFetch(serverUrl + '/updateMedication', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Medication", message: data, mode: "alert", onConfirm: null });
        getDocument();
        collapseForm();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function deleteDocument(currentObj) {
    const { _id: id } = currentObj;
    apiFetch(serverUrl + '/deleteMedication', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id: id }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Medication", message: data, mode: "alert", onConfirm: null });
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(id);
        setSelectedIds(newSelectedIds);
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  const createUpdate = (e) => {
    e.preventDefault();
    const nameVal = (medicationName != null ? String(medicationName) : '').trim();
    if (!nameVal) {
      setConfirmDialog({ open: true, title: "Medication", message: "Medication name is required.", mode: "alert", onConfirm: null });
      return;
    }
    if (edit) updateDocument();
    else createDocument();
  };

  const collapseForm = () => {
    setFormExpanded(false);
    setID(null);
    setMedicationName('');
    setBrandName('');
    setStrength('');
    setDosageForm('');
    setInstructions('');
    setPrescription(defaultPrescription());
    setPharmacy(defaultPharmacy());
    setScheduledTimesStr('');
    setStatus('active');
    setEdit(false);
  };

  const fillForm = (obj) => {
    setID(obj._id);
    setMedicationName(obj.medicationName || '');
    setBrandName(obj.brandName || '');
    setStrength(obj.strength || '');
    setDosageForm(obj.dosageForm || '');
    setInstructions(obj.instructions || '');
    setPrescription(obj.prescription && typeof obj.prescription === 'object' ? { ...defaultPrescription(), ...obj.prescription } : defaultPrescription());
    setPharmacy(obj.pharmacy && typeof obj.pharmacy === 'object' ? { ...defaultPharmacy(), ...obj.pharmacy } : defaultPharmacy());
    setScheduledTimesStr(Array.isArray(obj.prescription?.scheduledTimes) ? obj.prescription.scheduledTimes.join(', ') : '');
    setStatus(obj.status || 'active');
    setEdit(true);
    setFormExpanded(true);
  };

  function setPrescriptionField(field, value) {
    setPrescription((prev) => (prev ? { ...prev, [field]: value } : defaultPrescription()));
  }

  function setPharmacyField(field, value) {
    setPharmacy((prev) => (prev ? { ...prev, [field]: value } : defaultPharmacy()));
  }

  function handleSort(key) {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  }

  function getFilteredAndSortedData() {
    let filtered = (currentObjs || []).filter((obj) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (obj.medicationName && obj.medicationName.toLowerCase().includes(searchLower)) ||
        (obj.brandName && obj.brandName.toLowerCase().includes(searchLower)) ||
        (obj.strength && String(obj.strength).toLowerCase().includes(searchLower)) ||
        (obj.dosageForm && obj.dosageForm.toLowerCase().includes(searchLower)) ||
        (obj.instructions && obj.instructions.toLowerCase().includes(searchLower)) ||
        (obj.status && obj.status.toLowerCase().includes(searchLower))
      );
    });
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key] ?? '';
        const bValue = b[sortConfig.key] ?? '';
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  function handleSelectAll(e) {
    const filteredData = getFilteredAndSortedData();
    const newSelectedIds = new Set(selectedIds);
    if (e.target.checked) filteredData.forEach((obj) => newSelectedIds.add(obj._id));
    else filteredData.forEach((obj) => newSelectedIds.delete(obj._id));
    setSelectedIds(newSelectedIds);
  }

  function handleSelectOne(id, checked) {
    const newSelectedIds = new Set(selectedIds);
    if (checked) newSelectedIds.add(id);
    else newSelectedIds.delete(id);
    setSelectedIds(newSelectedIds);
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete medications", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete medications",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        let successCount = 0;
        let errorCount = 0;
        for (const id of idsToDelete) {
          try {
            const response = await apiFetch(serverUrl + '/deleteMedication', {
              method: 'POST',
              body: JSON.stringify({ account_id, _id: id }),
            });
            if (response.ok) successCount++;
            else errorCount++;
          } catch {
            errorCount++;
          }
        }
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: `Delete completed! Success: ${successCount}, Errors: ${errorCount}`,
          onConfirm: () => {
            setSelectedIds(new Set());
            getDocument();
            setDeleting(false);
          },
        });
      },
    });
  }

  const allFieldsFilled = Boolean(String(medicationName ?? '').trim());
  const filteredData = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredData);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <div className="page-container page-container-full page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Manage medications and prescriptions.">
        <ShieldCareSectionTabs activeTab={SHIELD_CARE_TAB_MEDICATIONS} />
      </SectionTabsPageHeader>
      <div className="page-content">
        <div className="page-top">
          <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
          <ConfirmationModal
            open={confirmDialog.open}
            title={confirmDialog.title || "Confirm"}
            message={confirmDialog.message}
        mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
        confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
            onConfirm={() => {
              const fn = confirmDialog.onConfirm;
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
              if (fn) fn();
            }}
            onCancel={() =>
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
            }
          />
          {formExpanded && (
            <div className="panel">
              <div className="panel-title">
                {edit ? 'Edit medication' : 'Add new medication'}{' '}
                <span className="panel-required-hint">(Medication name required)</span>
              </div>
              <div className="panel-row">
                <input type="text" value={medicationName ?? ''} placeholder="Medication name *" onChange={(e) => setMedicationName(e.target.value)} className="input input-min-140" />
                <input type="text" value={brandName ?? ''} placeholder="Brand name" onChange={(e) => setBrandName(e.target.value)} className="input input-min-120" />
                <input type="text" value={strength ?? ''} placeholder="Strength (e.g. 500 mg)" onChange={(e) => setStrength(e.target.value)} className="input input-min-100" />
                <input type="text" value={dosageForm ?? ''} placeholder="Dosage form (tablet, etc.)" onChange={(e) => setDosageForm(e.target.value)} className="input input-min-100" />
                <input type="text" value={instructions ?? ''} placeholder="Instructions" onChange={(e) => setInstructions(e.target.value)} className="input input-min-120" />
                <select value={status ?? 'active'} onChange={(e) => setStatus(e.target.value)} className="input select input-min-90">
                  <option value="active">active</option>
                  <option value="paused">paused</option>
                  <option value="completed">completed</option>
                </select>
              </div>
              <div className="panel-title panel-notes-row">Prescription</div>
              <div className="panel-row">
                <select value={prescription.doctorId ?? ''} onChange={(e) => setPrescriptionField('doctorId', e.target.value)} className="input select input-min-180">
                  <option value="">Select doctor</option>
                  {doctors.map((d) => (
                    <option key={d._id} value={d._id}>{d.name} {d.specialty ? `(${d.specialty})` : ''}</option>
                  ))}
                </select>
                <input type="text" value={prescription.startDate ?? ''} placeholder="Start date (YYYY-MM-DD)" onChange={(e) => setPrescriptionField('startDate', e.target.value)} className="input input-min-120" />
                <input type="text" value={prescription.endDate ?? ''} placeholder="End date (YYYY-MM-DD)" onChange={(e) => setPrescriptionField('endDate', e.target.value)} className="input input-min-120" />
                <input type="text" value={prescription.frequency ?? ''} placeholder="Frequency (daily, etc.)" onChange={(e) => setPrescriptionField('frequency', e.target.value)} className="input input-min-90" />
                <input type="text" value={prescription.timesPerDay ?? ''} placeholder="Times per day" onChange={(e) => setPrescriptionField('timesPerDay', e.target.value)} className="input input-min-80 input-number-no-spin" />
                <input type="text" value={scheduledTimesStr} placeholder="Scheduled times (08:00, 20:00)" onChange={(e) => setScheduledTimesStr(e.target.value)} className="input input-min-140" />
                <input type="text" value={prescription.quantityPerDose ?? ''} placeholder="Qty per dose" onChange={(e) => setPrescriptionField('quantityPerDose', e.target.value)} className="input input-min-70 input-number-no-spin" />
                <input type="text" value={prescription.totalPrescribedQuantity ?? ''} placeholder="Total quantity" onChange={(e) => setPrescriptionField('totalPrescribedQuantity', e.target.value)} className="input input-min-80 input-number-no-spin" />
                <input type="text" value={prescription.refillsAllowed ?? ''} placeholder="Refills" onChange={(e) => setPrescriptionField('refillsAllowed', e.target.value)} className="input input-min-60 input-number-no-spin" />
              </div>
              <div className="panel-title">Pharmacy</div>
              <div className="panel-row">
                <select value={pharmacy.pharmacyId ?? ''} onChange={(e) => setPharmacyField('pharmacyId', e.target.value)} className="input select input-min-180">
                  <option value="">Select pharmacy</option>
                  {pharmacies.map((p) => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
                <input type="text" value={pharmacy.lastRefillDate ?? ''} placeholder="Last refill (YYYY-MM-DD)" onChange={(e) => setPharmacyField('lastRefillDate', e.target.value)} className="input input-min-120" />
                <input type="text" value={pharmacy.daysSupply ?? ''} placeholder="Days supply" onChange={(e) => setPharmacyField('daysSupply', e.target.value)} className="input input-min-80 input-number-no-spin" />
                <input type="text" value={pharmacy.remainingQuantity ?? ''} placeholder="Remaining qty" onChange={(e) => setPharmacyField('remainingQuantity', e.target.value)} className="input input-min-80 input-number-no-spin" />
                <input type="text" value={pharmacy.refillReminderThresholdDays ?? ''} placeholder="Refill reminder (days)" onChange={(e) => setPharmacyField('refillReminderThresholdDays', e.target.value)} className="input input-min-100 input-number-no-spin" />
              </div>
              <div className="panel-row">
                <button type="button" onClick={(e) => createUpdate(e)} disabled={!allFieldsFilled} className="btn btn-primary">{edit ? 'Update' : 'Create'}</button>
                {edit && <button type="button" onClick={collapseForm} className="btn btn-secondary">Cancel</button>}
              </div>
            </div>
          )}
          <div className="toolbar">
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search medication, brand, strength, instructions, status…" className="input input-max-400" />
            <button type="button" onClick={() => setSearchTerm('')} className="btn btn-secondary btn-sm">Clear</button>
            <span className="toolbar-divider" />
            <button type="button" onClick={() => { if (formExpanded) collapseForm(); else setFormExpanded(true); }} className={`btn btn-md ${formExpanded ? 'btn-primary-outline' : 'btn-primary'}`}>{formExpanded ? '− New' : '+ New'}</button>
            <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0 || deleting} className="btn btn-danger">Delete{selectedCountSuffix}</button>
          </div>
        </div>
        <div className="table-scroll">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={filteredData.length > 0 && filteredData.every((obj) => selectedIds.has(obj._id))} onChange={handleSelectAll} disabled={filteredData.length === 0} />
                  </th>
                  <th className="sortable" onClick={() => handleSort('medicationName')}>Medication</th>
                  <th>Brand</th>
                  <th>Strength</th>
                  <th>Form</th>
                  <th>Instructions</th>
                  <th>Status</th>
                  <TableActionColumnHeader align="left" />
                </tr>
              </thead>
              <tbody>
                {filteredData.map((currentObj) => (
                  <tr key={currentObj._id}>
                    <td>
                      <input type="checkbox" checked={selectedIds.has(currentObj._id)} onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)} />
                    </td>
                    <td className="table-cell-ellipsis">{currentObj.medicationName || '—'}</td>
                    <td>{currentObj.brandName || '—'}</td>
                    <td>{currentObj.strength || '—'}</td>
                    <td>{currentObj.dosageForm || '—'}</td>
                    <td className="table-cell-ellipsis-sm" title={currentObj.instructions}>{currentObj.instructions || '—'}</td>
                    <td>{currentObj.status || '—'}</td>
                    <td>
                      <RowActionIcons>
                        <button type="button" onClick={() => fillForm(currentObj)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                        <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                      </RowActionIcons>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShieldCareMedications;
